import React, { useState, useEffect } from "react"
import { Link } from "react-router-dom"
import boardLogo from "@/images/board-logo.webp"
import { host } from "@/utils/utils"

interface LandingPageNavbarProps {
  showHomeLink?: boolean
}

const LandingPageNavbar: React.FC<LandingPageNavbarProps> = ({
  showHomeLink = false,
}) => {
  const [onlineCount, setOnlineCount] = useState<number | null>(null)

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const res = await fetch(`${host}/api/status/player-counts`)
        if (res.ok) {
          const data = await res.json()
          setOnlineCount(data.onlineUsersCount)
        }
      } catch (e) {
        // ignore
      }
    }
    fetchStats()
    const interval = setInterval(fetchStats, 20000)
    return () => clearInterval(interval)
  }, [])

  return (
    <nav className="w-full bg-[#f5e2c3e6] backdrop-blur-md z-50 border-b-4 border-[#a13a2b] shadow-lg fixed top-0 left-0 pt-[env(safe-area-inset-top,0px)] vintage-border">
      <div className="pl-[max(1.5rem,var(--safe-left))] pr-[max(1.5rem,var(--safe-right))] py-4 flex items-center justify-between">
        {/* Left: Logo and Game Name */}
        <div className="flex items-center gap-3">
          <img
            src={boardLogo}
            alt="Dealmaster Logo"
            className="h-12 w-12 object-contain"
          />
          <span className="text-3xl font-extrabold text-[#a13a2b] tracking-wide drop-shadow font-fourth vintage-text-shadow">
            <span className="text-[#4a2e19]">Deal</span>master
          </span>
        </div>

        {/* Center: Navigation Links */}
        <div className="hidden md:flex space-x-8 items-center">
          {showHomeLink && (
            <Link
              to="/"
              className="text-[#4a2e19] hover:text-[#a13a2b] transition-colors font-semibold vintage-link"
            >
              Home
            </Link>
          )}
          <Link
            to="/gallery"
            className="text-[#4a2e19] hover:text-[#a13a2b] transition-colors font-semibold vintage-link"
          >
            Gallery
          </Link>
          <Link
            to="/rules"
            className="text-[#4a2e19] hover:text-[#a13a2b] transition-colors font-semibold vintage-link"
          >
            Rules
          </Link>
        </div>

        {/* Right: Play Button, Online Count, and Discord */}
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2 bg-[#4a2e19] bg-opacity-20 rounded-lg px-3 py-2 border border-[#a13a2b]">
            <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
            <span className="text-[#4a2e19] font-semibold text-sm">
              {onlineCount !== null ? onlineCount : "..."} online
            </span>
          </div>
          {/* Play Now Button */}
          <Link
            to="/lobby"
            className="bg-gradient-to-r from-[#a13a2b] to-[#8b2e1f] hover:from-[#8b2e1f] hover:to-[#a13a2b] text-white font-bold px-6 py-2 rounded-lg transition-all duration-300 transform hover:scale-105 shadow-lg border-2 border-[#4a2e19] flex items-center gap-2 vintage-btn"
          >
            <i className="fas fa-play text-lg"></i>
            <span className="hidden sm:inline">Play Now</span>
            <span className="sm:hidden">Play</span>
          </Link>

          {/* Online Players Count */}

          {/* Discord Button */}
          <a
            href="https://discord.gg/your-server"
            target="_blank"
            rel="noopener noreferrer"
            className="bg-[#5865F2] hover:bg-[#4752C4] text-white font-semibold px-4 py-2 rounded-lg transition-all duration-300 transform hover:scale-105 shadow-lg border-2 border-[#4752C4] flex items-center gap-2 vintage-btn"
          >
            <i className="fab fa-discord text-lg"></i>
            <span className="hidden sm:inline">Join Discord</span>
            <span className="sm:hidden">Discord</span>
          </a>
        </div>
      </div>
    </nav>
  )
}

export default LandingPageNavbar
